import itemsToDo from '../../../../../assets/images/support/chart-reviews/items-to-do.png';
import manageTasks from '../../../../../assets/images/support/chart-reviews/manage-tasks.png';
import workspace from '../../../../../assets/images/support/chart-reviews/workspace.png';
import reviewQuestions from '../../../../../assets/images/support/chart-reviews/review-questions.png';
import pastSessions from '../../../../../assets/images/support/chart-reviews/past-sessions.png';
import flagChart from '../../../../../assets/images/support/chart-reviews/flag-chart.png';
import removeFlag from '../../../../../assets/images/support/chart-reviews/remove-flag.png';
import reviewSummary from '../../../../../assets/images/support/chart-reviews/review-summary.png';

export const content = `Use this guide to open your assigned chart reviews, answer the review questions for each chart, flag a chart for clinical review when needed, and submit the finished review.

> The screenshots below use sample names, dates, and results. Your assigned providers, review periods, and number of charts may be different.

## Workflow at a glance

1. Open the chart review task from **Items To Do**.
2. Select the assigned review from the **Manage** page.
3. Read each chart and answer every review question.
4. Flag a chart for clinical review when needed.
5. Repeat until every assigned chart is complete.
6. Review the summary, add feedback, and submit.

## Step 1: Open your chart review tasks

1. On your home page, scroll to **Items To Do**.
2. Find the chart review item. Current and overdue reviews may appear as separate items.
3. Select **Manage** next to the chart review you're ready to complete.

![Chart review items in Items To Do](${itemsToDo})

## Step 2: Select the assigned review

1. On the **Manage** page, find the assignment under **Tasks**.
2. Confirm the provider name, review month, and due or overdue status.
3. Select **Review Charts** for the assignment you want to open.

![Select the task, then Review Charts](${manageTasks})

You can also use the **Providers** section below **Tasks** to search by provider name or filter by state, but always open the assigned review from **Tasks**.

## Step 3: Get to know the chart review workspace

The workspace has four main areas:

* **A. Charts:** Select Chart 1, Chart 2, and the rest of the charts in the review. A check mark appears next to each completed chart.
* **B. Chart note:** Read the documentation for the selected chart. Scroll within this section as needed.
* **C. Review questions:** Answer the required questions for the selected chart.
* **D. View past sessions:** Open earlier sessions when you need more context.

The top of the page shows the review period, status, due date, completed-chart count, and the **Submit Chart** button. The top right of the chart shows when your work was last saved.

![Main areas of the chart review workspace](${workspace})

## Step 4: Complete each chart

1. Select the first chart in the left panel.
2. Read the chart note in the center panel.
3. Answer every question in the **Review Questions** panel. Responses are **Yes**, **Partially**, **No**, and **Not applicable**.
4. Scroll within the question panel until every required question is answered.
5. Select **Next Chart**.
6. Repeat for every chart in the assignment.
7. Confirm that completed charts show a check mark and the completed-chart count at the top of the page goes up.

![Answer every review question, then select Next Chart](${reviewQuestions})

### Optional: Review past sessions

1. Select **View past sessions** above the chart note.
2. Select a prior session from the list to see its details.
3. Return to the chart review once you have the context you need.

![Past Sessions shows earlier sessions and their details](${pastSessions})

## Step 5: Flag a chart for clinical review

1. Select the flag icon at the top right of the chart.
2. Choose a **Reason**, such as **Client Safety Concern** or **Other**.
3. Enter clear details explaining why you're flagging the chart.
4. Select **Save**.
5. Keep going with the review questions and remaining charts. Saved flags appear in the **Review Summary**.

![Select a reason, add details, and save the flag](${flagChart})

To remove a saved flag, select the **X** on the flag and confirm **Remove flag**.

![Confirm removal of a saved flag](${removeFlag})

## Step 6: Review the summary and submit

1. Select **Review Summary** in the left panel.
2. Review the **Overall Score**, the number of charts reviewed, and any charts flagged for clinical review.
3. Enter your notes and recommendations in the **Feedback for clinical team** field.
4. Confirm every assigned chart is complete and the completed-chart count is correct.
5. Select **Submit Chart** at the top right of the page.

![Add feedback in Review Summary, then submit](${reviewSummary})

If **Submit Chart** is gray, go back to the chart list and confirm every chart has a check mark and every required question is answered.

## Troubleshooting

* **I can't find the assignment.** Go to your home page, find the chart review item under **Items To Do**, and select **Manage**.
* **I need more clinical context.** Use **View past sessions** above the chart note.
* **I added a flag by mistake.** Select the **X** on the saved flag and confirm its removal.
* **Submit Chart is still gray.** Check every chart for a completion check mark and finish any unanswered review questions.

## Final checklist

1. Every assigned chart shows as complete.
2. Every required review question is answered.
3. Any flagged chart includes a reason and clear details.
4. Notes and recommendations are entered for the clinical team.
5. **Submit Chart** has been selected.
`;
